/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { Action, ReduxState } from '../types';
import { Dispatch } from 'redux';
import { BlendBridge } from 'blb-types/blendBridge';
import { getThreadsByLoanId, createThread, getThreadDetails, createMessage } from 'blb-api/loans';
import { getUserProfile } from 'blb-api/users';
import { omit, find } from 'lodash';

export enum InboxActionTypes {
  CREATE_NEW_MESSAGE_SUCCESS = 'blend-bridge/inbox/CREATE_NEW_MESSAGE_SUCCESS',
  FETCH_INBOX_THREAD_FAIL = 'blend-bridge/inbox/FETCH_INBOX_THREAD_FAIL',
  FETCH_INBOX_THREAD_SUCCESS = 'blend-bridge/inbox/FETCH_INBOX_THREAD_SUCCESS',
  FETCH_SELECTED_THREAD = 'blend-bridge/inbox/FETCH_SELECTED_THREAD',
  FETCH_SELECTED_THREAD_FAIL = 'blend-bridge/inbox/FETCH_SELECTED_THREAD_FAIL',
  FETCH_SELECTED_THREAD_SUCCESS = 'blend-bridge/inbox/FETCH_SELECTED_THREAD_SUCCESS',
  FETCH_USER_INFO_FAIL = 'blend-bridge/inbox/FETCH_USER_INFO_FAIL',
  FETCH_USER_INFO_SUCCESS = 'blend-bridge/inbox/FETCH_USER_INFO_SUCCESS',
  REMOVE_SELECTED_THREAD = 'blend-bridge/inbox/REMOVE_SELECTED_THREAD',
}

export const getThreadsForLoan = (loanId: string) => {
  return async (dispatch: Dispatch<Action>) => {
    try {
      const { data: threads } = await getThreadsByLoanId(loanId);
      dispatch({
        payload: threads,
        type: InboxActionTypes.FETCH_INBOX_THREAD_SUCCESS,
      });
    } catch (err) {
      dispatch({
        type: InboxActionTypes.FETCH_INBOX_THREAD_FAIL,
      });
    }
  };
};

export const fetchUserInfo = (principalId: string) => {
  return async (dispatch: Dispatch<Action>) => {
    try {
      const { data: fetchedUserInfo } = await getUserProfile(principalId);
      dispatch({
        payload: {
          firstName: fetchedUserInfo.firstName,
          id: principalId,
          lastName: fetchedUserInfo.lastName,
        },
        type: InboxActionTypes.FETCH_USER_INFO_SUCCESS,
      });
    } catch (err) {
      dispatch({
        type: InboxActionTypes.FETCH_USER_INFO_FAIL,
      });
    }
  };
};

export const removeSelectedThread = () => {
  return {
    type: InboxActionTypes.REMOVE_SELECTED_THREAD,
  };
};

export const setSelectedThread = (threadId: string, loanId: string) => {
  return async (dispatch: Dispatch<Action>) => {
    dispatch({
      payload: threadId,
      type: InboxActionTypes.FETCH_SELECTED_THREAD,
    });
    try {
      // Fetch messages/attachments associated with a thread
      const { data: thread } = await getThreadDetails(loanId, threadId);

      dispatch({
        payload: thread,
        type: InboxActionTypes.FETCH_SELECTED_THREAD_SUCCESS,
      });
    } catch (err) {
      dispatch({
        type: InboxActionTypes.FETCH_SELECTED_THREAD_FAIL,
      });
    }
  };
};

export const createThreadForLoan = (loanId: string, threadDetails: BlendBridge.API.PostInboxThreadBody) => {
  return async (dispatch: Dispatch<Action>) => {
    await createThread(loanId, threadDetails);
    // TODO - TITLE-496: Investigate why dispatch is not using the redux-thunk type
    dispatch<any>(getThreadsForLoan(loanId));
  };
};

export const createMessageForLoan = (
  loanId: string,
  threadId: string,
  messageDetails: BlendBridge.API.PostInboxMessageBody,
) => {
  return async (dispatch: Dispatch<Action>) => {
    const { data: message } = await createMessage(loanId, threadId, messageDetails);

    dispatch({
      payload: message,
      type: InboxActionTypes.CREATE_NEW_MESSAGE_SUCCESS,
    });

    dispatch<any>(getThreadsForLoan(loanId));
  };
};

export const initialState: ReduxState['inbox'] = {
  errors: {},
  initialLoad: true,
  selectedThread: null,
  threads: [],
  user: {},
};

export default (state = initialState, action: Action) => {
  switch (action.type) {
    case InboxActionTypes.CREATE_NEW_MESSAGE_SUCCESS:
      const message = action.payload;
      // non-null assertions below should not cause any issue.
      // createMessage may only be invoked when selectedThread is set
      // and messages are fetched.
      const messages = state.selectedThread!.messages!.concat(message);
      return {
        ...state,
        selectedThread: { ...state.selectedThread, messages },
      };
    case InboxActionTypes.FETCH_INBOX_THREAD_SUCCESS:
      return {
        ...state,
        errors: omit(state.errors, InboxActionTypes.FETCH_INBOX_THREAD_FAIL),
        initialLoad: false,
        threads: action.payload,
      };
    case InboxActionTypes.FETCH_INBOX_THREAD_FAIL:
      return {
        ...state,
        errors: {
          ...state.errors,
          [InboxActionTypes.FETCH_INBOX_THREAD_FAIL]: 'Failed to fetch threads. Please try again later.',
        },
      };
    case InboxActionTypes.FETCH_USER_INFO_SUCCESS:
      return {
        ...state,
        errors: omit(state.errors, InboxActionTypes.FETCH_USER_INFO_FAIL),
        user: action.payload,
      };
    case InboxActionTypes.FETCH_USER_INFO_FAIL:
      return {
        ...state,
        errors: {
          ...state.errors,
          [InboxActionTypes.FETCH_USER_INFO_FAIL]: 'Failed to fetch user. Please try again later.',
        },
      };
    case InboxActionTypes.FETCH_SELECTED_THREAD_SUCCESS:
      const selectedThread = state.selectedThread ? action.payload : null;
      return {
        ...state,
        errors: omit(state.errors, InboxActionTypes.FETCH_SELECTED_THREAD_FAIL),
        selectedThread,
      };
    case InboxActionTypes.FETCH_SELECTED_THREAD_FAIL:
      return {
        ...state,
        errors: {
          ...state.errors,
          [InboxActionTypes.FETCH_SELECTED_THREAD_FAIL]: 'Failed to fetch thread details. Please try again later.',
        },
        selectedThread: null,
      };
    case InboxActionTypes.REMOVE_SELECTED_THREAD:
      return { ...state, selectedThread: null };
    case InboxActionTypes.FETCH_SELECTED_THREAD:
      const thread = find(state.threads, { id: action.payload });
      return { ...state, selectedThread: thread };
    default:
      return state;
  }
};
